import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { History, RefreshCw, Filter } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { API, toast } from '@/App';

const PAGE_SIZE = 20;

const toIsoOrUndefined = (datetimeLocalValue) => {
  if (!datetimeLocalValue) {
    return undefined;
  }
  const parsed = new Date(datetimeLocalValue);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  return parsed.toISOString();
};

const toFriendlyDate = (rawDate) => {
  if (!rawDate) {
    return '-';
  }
  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) {
    return rawDate;
  }
  return parsed.toLocaleString();
};

const AuditLogsViewer = () => {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: PAGE_SIZE, total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    action: '',
    actor_id: '',
    status: '',
    from_date: '',
    to_date: '',
  });

  const requestParams = useMemo(() => {
    return {
      action: filters.action || undefined,
      actor_id: filters.actor_id || undefined,
      status: filters.status || undefined,
      from_date: toIsoOrUndefined(filters.from_date),
      to_date: toIsoOrUndefined(filters.to_date),
    };
  }, [filters]);

  const loadLogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/audit-logs`, {
        params: {
          page,
          page_size: PAGE_SIZE,
          ...requestParams,
        },
      });
      setLogs(response.data.items || []);
      setPagination(response.data.pagination || { page: 1, page_size: PAGE_SIZE, total: 0, total_pages: 1 });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de charger les audit logs');
    } finally {
      setLoading(false);
    }
  }, [requestParams]);

  useEffect(() => {
    loadLogs(1);
  }, [loadLogs]);

  const onApplyFilters = () => {
    loadLogs(1);
  };

  const onResetFilters = () => {
    const reset = {
      action: '',
      actor_id: '',
      status: '',
      from_date: '',
      to_date: '',
    };
    setFilters(reset);
    setTimeout(() => loadLogs(1), 0);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="w-5 h-5 text-cyan-600" />
          Audit Logs
        </CardTitle>
        <CardDescription>
          Consultation minimale des actions sensibles (filtrage et pagination).
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
          <Input
            placeholder="Action (ex: admin.user.delete)"
            value={filters.action}
            onChange={(event) => setFilters((prev) => ({ ...prev, action: event.target.value }))}
          />
          <Input
            placeholder="Actor ID"
            value={filters.actor_id}
            onChange={(event) => setFilters((prev) => ({ ...prev, actor_id: event.target.value }))}
          />
          <Input
            placeholder="Status (success, forbidden...)"
            value={filters.status}
            onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
          />
          <Input
            type="datetime-local"
            value={filters.from_date}
            onChange={(event) => setFilters((prev) => ({ ...prev, from_date: event.target.value }))}
          />
          <Input
            type="datetime-local"
            value={filters.to_date}
            onChange={(event) => setFilters((prev) => ({ ...prev, to_date: event.target.value }))}
          />
        </div>

        <div className="flex gap-2 mb-4">
          <Button onClick={onApplyFilters} className="bg-cyan-600 hover:bg-cyan-700">
            <Filter className="w-4 h-4 mr-2" />
            Filtrer
          </Button>
          <Button variant="outline" onClick={onResetFilters}>
            Réinitialiser
          </Button>
          <Button variant="outline" onClick={() => loadLogs(pagination.page)} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Acteur</TableHead>
              <TableHead>Ressource</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.id}>
                <TableCell className="text-sm">{toFriendlyDate(log.created_at)}</TableCell>
                <TableCell className="font-mono text-xs">{log.action}</TableCell>
                <TableCell>
                  <Badge className="bg-gray-100 text-gray-700">{log.status || '-'}</Badge>
                </TableCell>
                <TableCell className="text-xs">{log.actor_id || '-'}</TableCell>
                <TableCell className="text-xs">
                  {log.resource_type || '-'}
                  {log.resource_id ? `:${log.resource_id}` : ''}
                </TableCell>
              </TableRow>
            ))}
            {!loading && logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                  Aucun log trouvé avec ces filtres.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-600">
            Page {pagination.page} / {pagination.total_pages} ({pagination.total} entrées)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={loading || pagination.page <= 1}
              onClick={() => loadLogs(pagination.page - 1)}
            >
              Précédent
            </Button>
            <Button
              variant="outline"
              disabled={loading || pagination.page >= pagination.total_pages}
              onClick={() => loadLogs(pagination.page + 1)}
            >
              Suivant
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AuditLogsViewer;
